// Permanently deletes a tenant: backs up its schema to object storage, then
// drops it. Deliberately a separate dialog from ConfirmDialog — this one
// needs a typed-slug confirmation, not just Cancel/Confirm, and a 3-phase
// progress bar (dump -> upload -> cleanup) mirroring ProvisioningWizard's.
//
// Only ever rendered from TenantDetailScreen when the tenant is already
// 'suspended' — enforced there AND re-checked server-side (see
// routes/platform/tenants.js's POST /:id/delete) — so this dialog itself
// does not re-derive that gate, it trusts its caller.
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { streamDeletion, type StepEvent, type DeleteCompleteEvent } from '../lib/platformApi/provisionStream';

const PHASES: { step: string; label: string }[] = [
  { step: 'dump', label: 'Backing up schema' },
  { step: 'upload', label: 'Uploading backup' },
  { step: 'cleanup', label: 'Removing tenant data' },
];

interface DeleteTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: number;
  slug: string;
  onDeleted: () => void;
}

type Phase = 'confirm' | 'deleting' | 'done';

export function DeleteTenantDialog({ open, onOpenChange, tenantId, slug, onDeleted }: DeleteTenantDialogProps) {
  const [phase, setPhase] = useState<Phase>('confirm');
  const [typedSlug, setTypedSlug] = useState('');
  const [currentStep, setCurrentStep] = useState<StepEvent | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [result, setResult] = useState<DeleteCompleteEvent | null>(null);

  const currentPhaseIndex = currentStep ? PHASES.findIndex(p => p.step === currentStep.step) : -1;

  const reset = () => {
    setPhase('confirm'); setTypedSlug(''); setCurrentStep(null); setLog([]); setError(''); setResult(null);
  };

  const runDelete = async () => {
    setPhase('deleting'); setError('');
    try {
      await streamDeletion(tenantId, typedSlug, {
        onStep: (event) => { setCurrentStep(event); setLog(l => [...l, event.message]); },
        onComplete: (event) => { setResult(event); setPhase('done'); },
        onError: (event) => setError(event.message),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const close = (nextOpen: boolean) => {
    if (!nextOpen) {
      if (phase === 'done') onDeleted();
      reset();
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        {phase === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle>Delete tenant '{slug}' permanently?</DialogTitle>
              <DialogDescription>
                This backs up the tenant's schema to its own object storage, then permanently drops it from the
                database. This cannot be undone from this console — restoring from the backup afterward is a manual
                database operation. The tenant record itself is kept (marked deleted) so the backup location stays on
                record.
              </DialogDescription>
            </DialogHeader>
            <div style={{ margin: '4px 0 16px' }}>
              <label className="lbl">Type <code>{slug}</code> to confirm</label>
              <input value={typedSlug} onChange={e => setTypedSlug(e.target.value)} placeholder={slug} autoFocus />
            </div>
            {error && <div style={{ color: '#991B1B', fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="ghost">Cancel</Button>
              </DialogClose>
              <Button variant="danger" disabled={typedSlug !== slug} onClick={runDelete}>
                Delete permanently
              </Button>
            </DialogFooter>
          </>
        )}

        {phase === 'deleting' && (
          <>
            <DialogHeader>
              <DialogTitle>Deleting '{slug}'…</DialogTitle>
              <DialogDescription>Stay on this screen until this finishes.</DialogDescription>
            </DialogHeader>
            <div style={{ display: 'flex', gap: 6, margin: '4px 0 14px' }}>
              {PHASES.map((p, i) => (
                <div key={p.step} style={{
                  flex: 1, height: 8, borderRadius: 4,
                  background: i < currentPhaseIndex ? '#16A34A' : i === currentPhaseIndex ? '#DC2626' : '#E5E7EB',
                  transition: 'background .2s ease',
                }} />
              ))}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              {currentStep ? PHASES.find(p => p.step === currentStep.step)?.label ?? currentStep.message : 'Starting…'}
            </div>
            <div style={{
              background: '#0F172A', color: '#94A3B8', fontFamily: 'var(--mono)', fontSize: 11,
              borderRadius: 8, padding: 12, maxHeight: 180, overflowY: 'auto',
            }}>
              {log.map((line, i) => <div key={i}>{line}</div>)}
            </div>
            {error && (
              <div style={{ marginTop: 14 }}>
                <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 10 }}>
                  {error}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  If the backup step already completed, check the tenant's object storage before retrying — do not
                  assume nothing happened.
                </div>
              </div>
            )}
          </>
        )}

        {phase === 'done' && result && (
          <>
            <DialogHeader>
              <DialogTitle>Tenant '{result.slug}' deleted</DialogTitle>
              <DialogDescription>
                {result.tables} table(s) removed. Backup stored at <code>{result.dumpKey}</code> in the tenant's object storage.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
